/**
 * Element Feedback Form Component
 * Form for providing feedback on specific page areas using screenshot cropping
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, ElementFeedback, ElementSelectionState } from '../../../types/feedback';
import ScreenshotCropper from '../ScreenshotCropper';

interface ElementFeedbackFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<ElementFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
  elementSelection: ElementSelectionState;
  onElementSelectionChange: (selection: ElementSelectionState) => void;
  enableScreenshot: boolean;
}

const ElementFeedbackForm: React.FC<ElementFeedbackFormProps> = ({
  data,
  onChange,
  validation,
  elementSelection,
  onElementSelectionChange,
  enableScreenshot
}) => {
  const elementData = data as Partial<ElementFeedback>;
  const [isSelectingArea, setIsSelectingArea] = useState(false);
  const [selectedAreaInfo, setSelectedAreaInfo] = useState<any>(null);
  const [feedbackText, setFeedbackText] = useState(elementData.feedbackText || '');

  const handleStartSelection = () => {
    setIsSelectingArea(true);
  };

  const handleAreaSelected = (croppedImage: string, cropData: any) => {
    setIsSelectingArea(false);
    setSelectedAreaInfo({
      thumbnail: croppedImage,
      bounds: {
        x: cropData.x,
        y: cropData.y,
        width: cropData.width,
        height: cropData.height
      },
      fullScreenshot: cropData.fullScreenshot
    });
    
    // Update the form data with area information
    onChange({
      ...elementData,
      type: 'element-feedback',
      elementBounds: {
        x: cropData.x,
        y: cropData.y,
        width: cropData.width,
        height: cropData.height
      },
      screenshot: enableScreenshot ? croppedImage : undefined
    });
    
    // Update the element selection state
    onElementSelectionChange({
      isActive: false,
      selectedElement: undefined,
      screenshot: croppedImage
    });
  };

  const handleCancelSelection = () => {
    setIsSelectingArea(false);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setFeedbackText(text);
    onChange({
      ...elementData,
      feedbackText: text,
      type: 'element-feedback'
    });
  };

  const handleRemoveArea = () => {
    setSelectedAreaInfo(null);
    onChange({
      ...elementData,
      elementBounds: undefined,
      screenshot: undefined
    });
    onElementSelectionChange({
      isActive: false,
      selectedElement: undefined,
      screenshot: undefined
    });
  };

  return (
    <>
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-serif text-slate-800 mb-2">
            Area-Specific Feedback
          </h3>
          <p className="text-sm text-slate-600">
            Capture and select a specific area of the page to provide feedback
          </p>
        </div>

        {/* Area Selection Section */}
        <div className="space-y-4">
          {!selectedAreaInfo ? (
            <button
              onClick={handleStartSelection}
              className="w-full py-4 px-6 bg-gradient-to-r from-warm-500 to-warm-400 text-white hover:from-warm-600 hover:to-warm-500 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <div className="flex items-center justify-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="5" y="5" width="14" height="14" rx="2"/>
                  <path d="M9 9h6v6h-6z"/>
                </svg>
                <span className="font-medium">Capture & Select Area</span>
              </div>
              <p className="text-sm opacity-90 mt-1">
                Take a screenshot and select the specific area
              </p>
            </button>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-600">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span className="font-medium text-green-800">Area Selected</span>
                  </div>
                  <div className="space-y-1 text-sm text-green-700">
                    <p>
                      <span className="font-medium">Area:</span> {Math.round(selectedAreaInfo.bounds.width)}×{Math.round(selectedAreaInfo.bounds.height)}px
                    </p>
                    <p>
                      <span className="font-medium">Position:</span> ({Math.round(selectedAreaInfo.bounds.x)}, {Math.round(selectedAreaInfo.bounds.y)})
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveArea}
                  className="text-red-600 hover:text-red-700 p-1"
                  title="Remove selection"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              
              {/* Show thumbnail if available */}
              {selectedAreaInfo.thumbnail && enableScreenshot && (
                <div className="mt-3 border-t border-green-200 pt-3">
                  <p className="text-xs text-green-700 mb-2">Selected Area Preview:</p>
                  <img 
                    src={selectedAreaInfo.thumbnail} 
                    alt="Selected area"
                    className="max-w-full h-auto rounded border border-green-300 shadow-sm"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Feedback Text */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Your Feedback {selectedAreaInfo ? 'About This Area' : ''}
            </label>
            <textarea
              placeholder="Describe your feedback about a specific area of this page (optionally capture an area below)"
              className="input-minimal resize-none"
              rows={4}
              value={feedbackText}
              onChange={handleTextChange}
            />
            {validation.errors.feedbackText && (
              <p className="text-sm text-red-600 mt-1">{validation.errors.feedbackText}</p>
            )}
          </div>

          {/* Additional Options - Always visible */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-warm-500 border-gray-300 focus:ring-warm-500"
                checked={elementData.isVisualBug || false}
                onChange={(e) => onChange({ ...elementData, isVisualBug: e.target.checked })}
              />
              <span className="text-sm text-slate-700">This is a visual/display issue</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-warm-500 border-gray-300 focus:ring-warm-500"
                checked={elementData.isFunctionalBug || false}
                onChange={(e) => onChange({ ...elementData, isFunctionalBug: e.target.checked })}
              />
              <span className="text-sm text-slate-700">This area doesn't work properly</span>
            </label>
            
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 text-warm-500 border-gray-300 focus:ring-warm-500"
                checked={elementData.needsImprovement || false}
                onChange={(e) => onChange({ ...elementData, needsImprovement: e.target.checked })}
              />
              <span className="text-sm text-slate-700">This could be improved</span>
            </label>
          </div>
        </div>

        {/* Help Text */}
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="flex items-start gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600 mt-0.5">
              <circle cx="12" cy="12" r={10}/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="space-y-1 text-xs">
                <li>• Click "Capture & Select Area" to take a screenshot</li>
                <li>• Click and drag to select the specific area you want to comment on</li>
                <li>• Resize the selection by dragging the corners</li>
                <li>• Confirm your selection and add your feedback</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Screenshot Cropper Overlay */}
      <ScreenshotCropper
        isActive={isSelectingArea}
        onAreaSelected={handleAreaSelected}
        onCancel={handleCancelSelection}
        title="Select Area for Feedback"
        description="Click and drag to select the area you want to provide feedback about"
      />
    </>
  );
};

export default ElementFeedbackForm;